/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/Utils/OAuthHelper" />

import * as Constants from "DistributedTaskUI/ServiceEndpoints/Editor/Constants";
import { ServiceEndpointsSource } from "DistributedTaskUI/ServiceEndpoints/Editor/Source/ServiceEndpointsSource";
import {
    OAuthConfiguration,
    OAuthConfigurationParams,
    ServiceEndpoint
} from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

export interface IOAuthCreationProps {
    oAuthCreatedAndAuthorizedCallback: (configId?: string, error?: string) => void;
    newlyCreatedOauthConfigId?: string;
    shouldUpdateWholeOAuthConfig: boolean;
}

export interface IGenericAuthorizationOAuthProps {
    buttonComponent?: (authorizeCallback: (configId?: string) => void) => JSX.Element;
    oAuthCreatedAndAuthorizedCallback?: (configId?: string, error?: string, editShouldDelete?: boolean) => void;
    createdOAuthUrl?: string;
    oAuthHelper: OAuthHelper;
}

export const enum OAuthConfigOption {
    ExistingOAuth = 1,
    CreateOAuth = 2
}

export class OAuthHelper {
    private _selectedOAuthConfigOption: OAuthConfigOption;
    private _serviceEndpointComponentIsInvalid: boolean;
    
    constructor() {
        this._selectedOAuthConfigOption = OAuthConfigOption.ExistingOAuth;
    }

    public isNewOAuthUx(authorizationScheme: string) {
        return authorizationScheme === Constants.OAuth2Scheme;
    }

    public isUseExistingOAuthUx(authorizationScheme: string) {
        return this.isNewOAuthUx(authorizationScheme) && this._selectedOAuthConfigOption === OAuthConfigOption.ExistingOAuth;
    }

    public isCreateNewOAuthUx(authorizationScheme: string) {
        return this.isNewOAuthUx(authorizationScheme) && this._selectedOAuthConfigOption === OAuthConfigOption.CreateOAuth;
    }

    public updateOAuthConfigOption(option: OAuthConfigOption) {
        this._selectedOAuthConfigOption = option;
    }

    public getOAuthConfigOption() {
        return this._selectedOAuthConfigOption;
    }

    public getServiceEndpointComponentIsInvalid() {
        return this._serviceEndpointComponentIsInvalid;
    }
    public updateServiceEndpointComponentIsInvalid(value: boolean) {
        this._serviceEndpointComponentIsInvalid = value;
    }
}

export function createOAuthConfiguration(
    draftServiceEndpoint: ServiceEndpoint,
    source: ServiceEndpointsSource,
    shouldDeleteOAuthConfigBeforeCreate: boolean,
    updateDraftEndpointState: (draftEndpoint: ServiceEndpoint) => void,
    onAuthorizeClick: (configurationId: string) => void,
    oAuthCreationProps: IOAuthCreationProps,
    params: OAuthConfigurationParams
): void {
    if (shouldDeleteOAuthConfigBeforeCreate && oAuthCreationProps.newlyCreatedOauthConfigId) {
        source
            .deleteOAuthConfiguration(oAuthCreationProps.newlyCreatedOauthConfigId)
            .then((configuration: OAuthConfiguration) => {
                createOAuthConfiguration(draftServiceEndpoint, source, false, updateDraftEndpointState, onAuthorizeClick, oAuthCreationProps, params);
            })
            .catch((error: any) => {
                oAuthCreationProps.oAuthCreatedAndAuthorizedCallback(null, error);
            });
    } else {
        source
            .createOAuthConfigurations(params)
            .then((configuration: OAuthConfiguration) => {
                draftServiceEndpoint.authorization.parameters[Constants.ConfigurationId] = configuration.id;
                // after oauth is saved, draftServiceEndpoint.url is the url of the new oauth configuration
                draftServiceEndpoint.url = params.url;
                updateDraftEndpointState(draftServiceEndpoint);
                onAuthorizeClick(configuration.id);
            })
            .catch(error => {
                oAuthCreationProps.oAuthCreatedAndAuthorizedCallback(null, error);
            });
    }
}

export function editNewOAuthConfiguration(
    draftServiceEndpoint: ServiceEndpoint,
    source: ServiceEndpointsSource,
    updateDraftEndpointState: (draftEndpoint: ServiceEndpoint) => void,
    onAuthorizeClick: (configurationId?: string) => void,
    oAuthCreationProps: IOAuthCreationProps,
    params: OAuthConfigurationParams
): void {
    if (oAuthCreationProps.shouldUpdateWholeOAuthConfig) {
        // If user is updating more than just the name property of the newly created oauth, we will delete and recreate the oauth configuration.
        createOAuthConfiguration(draftServiceEndpoint, source, true, updateDraftEndpointState, onAuthorizeClick, oAuthCreationProps, params);
    } else {
        const editParams: OAuthConfigurationParams = {
            name: params.name,
            endpointType: params.endpointType,
            url: null,
            clientId: null,
            clientSecret: null
        };

        source
            .updateOAuthConfigurations(editParams, oAuthCreationProps.newlyCreatedOauthConfigId)
            .then((configuration: OAuthConfiguration) => {
                draftServiceEndpoint.authorization.parameters[Constants.ConfigurationId] = configuration.id;
                updateDraftEndpointState(draftServiceEndpoint);
                onAuthorizeClick();
            })
            .catch(error => {
                oAuthCreationProps.oAuthCreatedAndAuthorizedCallback(null, error);
            });
    }
}
